import { Injectable } from "@angular/core";
import { ChartHistoricCategoryType } from "../../../../_shared/types/charts.types";
import {
    CompanyEntity,
    SerieEntity,
    VisualizationEntity
} from "../../../../../../src/arch/domain/entity";
import { ColorsService } from "../colors.service";
import { GetIndicatorComparationsUsecase } from "../../usecase/get-indicator-comparations.usecase";
import { VisualizationOptionsType } from "../../../../../../src/arch/_shared/types/options-visualization";
import { PointMapRepository } from "../../../../../../src/arch/infra/repository/map";
import { IndicatorComparationsResultType } from "../../usecase/get-indicator-comparations.usecase";

enum ChartType {
    AREASPLINE = "areaspline",
    COLUMN = "column",
    LINE = "line"
}

interface ChartColors {
    polarityColor: string;
    color: string;
}

interface SeriesReference {
    _id: string;
    type: string;
    color: string;
    name: string;
}

@Injectable({
    providedIn: "root"
})
export class ChartHistoricFormatterService {
    private readonly pointRepository = new PointMapRepository();

    private getStrategy(
        strategy: ChartHistoricCategoryType
    ): IChartHistoricStrategy {
        const strategies = {
            [ChartType.AREASPLINE]: new AreaSplineChartHistoricStrategy(),
            [ChartType.COLUMN]: new ColumnChartHistoricStrategy(),
            [ChartType.LINE]: new LineChartHistoricStrategy()
        };
        const selectedStrategy = strategies[strategy];
        if (!selectedStrategy) {
            throw new Error(`Estratégia de gráfico não suportada: ${strategy}`);
        }
        return selectedStrategy;
    }

    public formatter(
        strategy: ChartHistoricCategoryType,
        series: SerieEntity[],
        company: CompanyEntity,
        options: VisualizationOptionsType,
        visualization: VisualizationEntity,
        comparations: IndicatorComparationsResultType[]
    ): Highcharts.Options {
        if (!series?.length) {
            throw new Error("Séries de dados não fornecidas");
        }
        const _strategy: IChartHistoricStrategy = this.getStrategy(strategy);
        const colors = {
            polarityColor: comparations[0].polarityColor,
            color: ColorsService.getColor(comparations[0].polarityColor)
        };
        const needReversed = this.needReversed(series);
        const structuredChart = _strategy.format(
            series,
            company,
            options,
            visualization,
            colors,
            needReversed
        );
        return structuredChart;
    }

    private needReversed(series: SerieEntity[]): boolean {
        if (!series?.length) {
            return false;
        }
        return (
            series.length === 1 &&
            series[0]?.props?.metadata?.natureOfAccount === "negative"
        );
    }
}

abstract class BaseChartHistoricStrategy implements IChartHistoricStrategy {
    protected getBaseConfig(
        colors: ChartColors,
        needReversed: boolean
    ): Partial<Highcharts.Options> {
        return {
            legend: {
                enabled: false
            },
            plotOptions: {
                series: {
                    marker: {
                        enabled: true,
                        radius: 2
                    },
                    animation: {
                        duration: 1000
                    },
                    states: {
                        hover: {
                            enabled: true,
                            opacity: 1
                        }
                    }
                }
            },
            yAxis: {
                reversed: needReversed,
                title: {
                    text: ""
                }
            }
        };
    }

    protected getSeriesReferences(
        series: SerieEntity[],
        options: VisualizationOptionsType,
        visualization: VisualizationEntity,
        colors: ChartColors,
        type: string
    ): SeriesReference[] {
        if (options.goals && visualization.props?.historic) {
            return visualization.props.historic.map((serieChart) => {
                const serie = series.find(
                    (s) => s.props._id === serieChart.dataSerieId
                );
                return {
                    _id: serie.props._id,
                    type,
                    color: colors.color,
                    name: serie.props.name
                };
            });
        }
        return series.map((s) => ({
            _id: s.props._id,
            type,
            color: colors.polarityColor,
            name: s.props.name
        }));
    }

    abstract format(
        series: SerieEntity[],
        company: CompanyEntity,
        options: VisualizationOptionsType,
        visualization: VisualizationEntity,
        colors: ChartColors,
        needReversed: boolean
    ): Highcharts.Options;
}

class AreaSplineChartHistoricStrategy extends BaseChartHistoricStrategy {
    public format(
        series: SerieEntity[],
        company: CompanyEntity,
        options: VisualizationOptionsType,
        visualization: VisualizationEntity,
        colors: ChartColors,
        needReversed: boolean
    ): Highcharts.Options {
        const baseConfig = this.getBaseConfig(colors, needReversed);
        const seriesReferences = this.getSeriesReferences(
            series,
            options,
            visualization,
            colors,
            ChartType.AREASPLINE
        );
        return {
            ...baseConfig,
            chart: {
                type: ChartType.AREASPLINE,
                reflow: true,
                margin: 0,
                backgroundColor: null,
                spacingTop: -3,
                spacingRight: -3,
                spacingBottom: -3,
                spacingLeft: -3
            },
            plotOptions: {
                ...baseConfig.plotOptions,
                areaspline: {
                    lineWidth: 0,
                    fillOpacity: 0.16,
                    marker: {
                        enabled: false
                    },
                    states: {
                        hover: {
                            lineWidth: 0
                        }
                    }
                },
                series: {
                    ...baseConfig.plotOptions?.series,
                    color: colors.color,
                    enableMouseTracking: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    },
                    allowPointSelect: false,
                    cursor: "default"
                }
            },
            series: seriesReferences as Highcharts.SeriesOptionsType[],
            yAxis: {
                ...baseConfig.yAxis,
                labels: {
                    enabled: false
                },
                gridLineWidth: 0
            },
            xAxis: {
                labels: {
                    enabled: false
                },
                lineWidth: 0,
                tickLength: 0,
                minPadding: 0,
                maxPadding: 0
            },
            tooltip: {
                enabled: false
            },
            accessibility: {
                enabled: false
            }
        };
    }
}

class ColumnChartHistoricStrategy extends BaseChartHistoricStrategy {
    public format(
        series: SerieEntity[],
        company: CompanyEntity,
        options: VisualizationOptionsType,
        visualization: VisualizationEntity,
        colors: ChartColors,
        needReversed: boolean
    ): Highcharts.Options {
        const baseConfig = this.getBaseConfig(colors, needReversed);
        const seriesReferences = this.getSeriesReferences(
            series,
            options,
            visualization,
            colors,
            ChartType.COLUMN
        );
        return {
            ...baseConfig,
            series: seriesReferences as Highcharts.SeriesOptionsType[],
            chart: {
                reflow: true,
                type: ChartType.COLUMN
            },
            yAxis: {
                ...baseConfig.yAxis,
                plotLines: [
                    {
                        width: 1,
                        value: 0,
                        zIndex: 6
                    }
                ],
                labels: {
                    enabled: true
                }
            }
        };
    }
}

class LineChartHistoricStrategy extends BaseChartHistoricStrategy {
    public format(
        series: SerieEntity[],
        company: CompanyEntity,
        options: VisualizationOptionsType,
        visualization: VisualizationEntity,
        colors: ChartColors,
        needReversed: boolean
    ): Highcharts.Options {
        const baseConfig = this.getBaseConfig(colors, needReversed);
        const seriesReferences = this.getSeriesReferences(
            series,
            options,
            visualization,
            colors,
            ChartType.LINE
        );
        return {
            ...baseConfig,
            chart: {
                type: ChartType.LINE,
                reflow: true
            },
            plotOptions: {
                series: {
                    color: colors.color
                }
            },
            yAxis: {
                ...baseConfig.yAxis,
                plotLines: [
                    {
                        width: 1,
                        value: 0,
                        zIndex: 6
                    }
                ],
                labels: {
                    enabled: true,
                    format: "{value}"
                }
            },
            series: seriesReferences as Highcharts.SeriesOptionsType[]
        };
    }
}

interface IChartHistoricStrategy {
    format(
        series: SerieEntity[],
        company: CompanyEntity,
        options: VisualizationOptionsType,
        visualization: VisualizationEntity,
        colors: {
            polarityColor: string;
            color: string;
        },
        needReversed: boolean
    ): Highcharts.Options;
}

// const _formatLabel = (x: Date) => {
//     const period = this.options.period;
//     if (["yearly", "quarterly", "semester"].includes(period)) {
//         return this.getPeriodLabels(x, period);
//     }
//     return DateUtils.dateToString(x, "MMMM 'de' yyyy");
// };

// private getPeriodLabels(date: Date, period: string) {
//     const year = DateUtils.dateToString(date, "yyyy");
//     if (period == "quarterly") {
//         const response = `${DateUtils.nameQuarterly(date, true)} ${year}`;
//         return response;
//     }
//     if (period == "semester") {
//         const response = `${DateUtils.nameSemester(date, true)} ${year}`;
//         return response;
//     }
//     return year;
// }
